/**
 * @fileoverview Task list column visibility menu (MOR-707)
 *
 * Popover listing every list column with a check toggle — same pattern as
 * the precedent library's Columns control. The hosting surface owns the
 * visible-set state (persisted to localStorage).
 */

import { useState } from "react";
import { Check, Columns3 } from "lucide-react";

import { TASK_LIST_COLUMNS } from "./task-list-view.helpers";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

export function TaskColumnsMenu({
  visible,
  onToggle,
  crossJob = false,
}: {
  visible: ReadonlySet<string>;
  onToggle: (key: string) => void;
  /** Cross-job workspace view — surfaces the Job column. */
  crossJob?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // The Job column only applies to the cross-job workspace view.
  const cols = TASK_LIST_COLUMNS.filter((c) => !c.crossJobOnly || crossJob);
  const visibleCount = cols.filter((c) => visible.has(c.key)).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <Columns3 className="size-3.5" />
          Columns
          <Badge variant="secondary" className="ml-0.5 text-[10px]">
            {visibleCount}/{cols.length}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {cols.map((c) => {
          const isVisible = visible.has(c.key);
          const locked = c.key === "title" || c.key === "job";
          return (
            <button
              key={c.key}
              type="button"
              disabled={locked}
              onClick={() => onToggle(c.key)}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent",
                locked && "cursor-default opacity-60",
              )}
            >
              <span
                className={cn(
                  "flex size-4 items-center justify-center rounded border",
                  isVisible
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background",
                )}
              >
                {isVisible && <Check className="size-3" />}
              </span>
              <span className="text-primary/80">{c.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
